import React, { useEffect } from "react";
import MetaHead from "./MetaHead";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { trackPageVisit } from "../utils/analytics";
import Navbar from "./Navbar";
import SocialHandles from "./SocialHandles";

const Guide: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    trackPageVisit("guide");
  }, []);

  return (
    <>
      <MetaHead
        title={t('guide.title')}
        description={`${t('guide.description') as string} - ${t('guide.freeMetaDescription') as string}`}
        keywords={t('guide.keywords') as string}
      />
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900 p-4">
        <Navbar pageTitle="Guide" />

        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-glow mb-6">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary-600 via-accent-600 to-secondary-600 bg-clip-text text-transparent mb-4">
              {t('guide.mainTitle')}
            </h1>
            <p className="text-lg text-neutral-600 dark:text-neutral-300 mb-6 max-w-2xl mx-auto">
              {t('guide.subtitle')}
            </p>

            {/* Free to Use Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm font-semibold rounded-full shadow-lg mb-4">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {t("app.freeToUse")}
            </div>
          </div>

          {/* Motivation Quote */}
          <div className="glass-card p-6 rounded-2xl mb-8 text-center">
            <blockquote className="text-xl italic text-neutral-700 dark:text-neutral-200 mb-4">
              "{t('guide.quote')}"
            </blockquote>
            <cite className="text-neutral-500 dark:text-neutral-400">
              {t('guide.quoteAuthor')}
            </cite>
          </div>

          {/* Core Features */}
          <div className="space-y-8 mt-12">
            {/* Habit Tracking */}
            <section className="glass-card p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-xl flex items-center justify-center mr-4">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {t('guide.habitTrackingTitle')}
                </h2>
              </div>
              <p className="text-neutral-600 dark:text-neutral-300 mb-4">
                {t('guide.habitTrackingDesc')}
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                    {t('guide.keyFeatures')}
                  </h3>
                  <ul className="space-y-2 text-neutral-600 dark:text-neutral-300">
                    <li> {t('guide.createRecurringHabits')}</li>
                    <li> {t('guide.scheduleDays')}</li>
                    <li> {t('guide.visualStreaks')}</li>
                    <li> {t('guide.interactiveCheckboxes')}</li>
                    <li> {t('guide.streakRecovery')}</li>
                    <li> {t('guide.achievementMilestones')}</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                    {t('guide.smartStatusDisplay')}
                  </h3>
                  <ul className="space-y-2 text-neutral-600 dark:text-neutral-300">
                    <li> {t('guide.completed')}</li>
                    <li> {t('guide.missed')}</li>
                    <li> {t('guide.streak')}</li>
                    <li> {t('guide.future')}</li>
                  </ul>
                </div>
              </div>
              <div className="mt-6 p-4 bg-gradient-to-r from-secondary-50 to-primary-50 dark:from-secondary-900/20 dark:to-primary-900/20 rounded-xl border border-secondary-200 dark:border-secondary-800">
                <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                  {t('guide.smartHabitScheduling')}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300">
                  {t('guide.schedulingDesc')}
                </p>
              </div>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <img src="/assets/11-habit-list.png" alt="Habit List Screenshot" className="rounded-xl shadow-lg w-full" />
                <img src="/assets/13-app-day-view-with-bingo.png" alt="Day View with Bingo Screenshot" className="rounded-xl shadow-lg w-full" />
              </div>
            </section>

            {/* Todo Management */}
            <section className="glass-card p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center mr-4">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {t('guide.taskManagementTitle')}
                </h2>
              </div>
              <p className="text-neutral-600 dark:text-neutral-300 mb-4">
                {t('guide.taskManagementDesc')}
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                    {t('guide.todoFeatures')}
                  </h3>
                  <ul className="space-y-2 text-neutral-600 dark:text-neutral-300">
                    <li> {t('guide.createDueDates')}</li>
                    <li> {t('guide.markComplete')}</li>
                    <li> {t('guide.editDelete')}</li>
                    <li> {t('guide.priorityLevels')}</li>
                    <li> {t('guide.quickCreation')}</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                    {t('guide.calendarIntegration')}
                  </h3>
                  <ul className="space-y-2 text-neutral-600 dark:text-neutral-300">
                    <li> {t('guide.visualIndicators')}</li>
                    <li> {t('guide.todoBadges')}</li>
                    <li> {t('guide.detailedView')}</li>
                    <li> {t('guide.colorCodedPriority')}</li>
                  </ul>
                </div>
              </div>
              <div className="mt-6">
                <img src="/assets/10-todo-list.png" alt="Todo List Screenshot" className="rounded-xl shadow-lg w-full md:max-w-md md:mx-auto md:block" />
              </div>
            </section>

            {/* Notes System */}
            <section className="glass-card p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mr-4">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {t('guide.notesTitle')}
                </h2>
              </div>
              <p className="text-neutral-600 dark:text-neutral-300 mb-4">
                {t('guide.notesDesc')}
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                    {t('guide.noteFeatures')}
                  </h3>
                  <ul className="space-y-2 text-neutral-600 dark:text-neutral-300">
                    <li> {t('guide.dateSpecificNotes')}</li>
                    <li> {t('guide.tagOrganization')}</li>
                    <li> {t('guide.searchFilter')}</li>
                    <li> {t('guide.archiveDelete')}</li>
                    <li> <strong>{t('guide.visibilityControl')}</strong>: {t('guide.visibilityControlDesc')}</li>
                    <li> <strong>{t('guide.publicSharing')}</strong>: {t('guide.publicSharingDesc')}</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                    {t('guide.integration')}
                  </h3>
                  <ul className="space-y-2 text-neutral-600 dark:text-neutral-300">
                    <li> {t('guide.notesOnCalendar')}</li>
                    <li> {t('guide.visualNoteIndicators')}</li>
                    <li> {t('guide.quickNoteCreation')}</li>
                    <li> {t('guide.detailedNoteView')}</li>
                    <li> <strong>{t('guide.publicAccess')}</strong>: {t('guide.publicAccessDesc')}</li>
                    <li> <strong>{t('guide.ownerAttribution')}</strong>: {t('guide.ownerAttributionDesc')}</li>
                  </ul>
                </div>
              </div>
              <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                  {t('guide.publicNoteSharingTitle')}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300 mb-3">
                  {t('guide.publicNoteSharingDesc')}
                </p>
                <ul className="space-y-1 text-neutral-600 dark:text-neutral-300 text-sm">
                  <li> <strong>{t('guide.privacyControl')}</strong>: {t('guide.privacyControlDesc')}</li>
                  <li> <strong>{t('guide.oneClickSharing')}</strong>: {t('guide.oneClickSharingDesc')}</li>
                  <li> <strong>{t('guide.noAuthenticationRequired')}</strong>: {t('guide.noAuthenticationRequiredDesc')}</li>
                  <li> <strong>{t('guide.creatorAttribution')}</strong>: {t('guide.creatorAttributionDesc')}</li>
                </ul>
              </div>
              <div className="mt-6">
                <img src="/assets/12-note-details.png" alt="Note Details Screenshot" className="rounded-xl shadow-lg w-full md:max-w-md md:mx-auto md:block" />
              </div>
            </section>

            {/* Journaling System */}
            <section className="glass-card p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mr-4">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {t('guide.journalingTitle')}
                </h2>
              </div>
              <p className="text-neutral-600 dark:text-neutral-300 mb-4">
                {t('guide.journalingDesc')}
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                    {t('guide.journalFeatures')}
                  </h3>
                  <ul className="space-y-2 text-neutral-600 dark:text-neutral-300">
                    <li> {t('guide.dailyJournaling')}</li>
                    <li> {t('guide.moodTracking')}</li>
                    <li> {t('guide.encryptedContent')}</li>
                    <li> {t('guide.tagOrganization')}</li>
                    <li> {t('guide.oneEntryPerDay')}</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                    {t('guide.journalBenefits')}
                  </h3>
                  <ul className="space-y-2 text-neutral-600 dark:text-neutral-300">
                    <li> {t('guide.selfReflection')}</li>
                    <li> {t('guide.emotionalAwareness')}</li>
                    <li> {t('guide.privacySecurity')}</li>
                    <li> {t('guide.calendarIntegration')}</li>
                    <li> {t('guide.personalGrowth')}</li>
                  </ul>
                </div>
              </div>
              <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
                <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                  {t('guide.journalSecurity')}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300">
                  {t('guide.journalSecurityDesc')}
                </p>
              </div>
            </section>

            {/* Reminders */}
            <section className="glass-card p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mr-4">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {t('guide.remindersTitle')}
                </h2>
              </div>
              <p className="text-neutral-600 dark:text-neutral-300 mb-4">
                {t('guide.remindersDesc')}
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                    {t('guide.reminderTypes')}
                  </h3>
                  <ul className="space-y-2 text-neutral-600 dark:text-neutral-300">
                    <li> {t('guide.oneTimeReminders')}</li>
                    <li> {t('guide.recurringReminders')}</li>
                    <li> {t('guide.timeBasedScheduling')}</li>
                    <li> {t('guide.priorityStyling')}</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                    {t('guide.management')}
                  </h3>
                  <ul className="space-y-2 text-neutral-600 dark:text-neutral-300">
                    <li> {t('guide.snoozeDismiss')}</li>
                    <li> {t('guide.editPatterns')}</li>
                    <li> {t('guide.visualBadges')}</li>
                    <li> {t('guide.dailyViewIntegration')}</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Data Tracking */}
            <section className="glass-card p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-xl flex items-center justify-center mr-4">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {t('guide.dataTrackingTitle')}
                </h2>
              </div>
              <p className="text-neutral-600 dark:text-neutral-300 mb-4">
                {t('guide.dataTrackingDesc')}
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                    {t('guide.trackerTypes')}
                  </h3>
                  <ul className="space-y-2 text-neutral-600 dark:text-neutral-300">
                    <li> {t('guide.numberTracking')}</li>
                    <li> {t('guide.stringValues')}</li>
                    <li> {t('guide.booleanTracking')}</li>
                    <li> {t('guide.timeTracking')}</li>
                    <li> {t('guide.customUnits')}</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                    {t('guide.advancedFeatures')}
                  </h3>
                  <ul className="space-y-2 text-neutral-600 dark:text-neutral-300">
                    <li> {t('guide.selectiveTracking')}</li>
                    <li> {t('guide.dailyDataEntry')}</li>
                    <li> {t('guide.historicalVisualization')}</li>
                    <li> {t('guide.progressCharts')}</li>
                    <li> {t('guide.smartCalendarIntegration')}</li>
                  </ul>
                </div>
              </div>
              <div className="mt-6 p-4 bg-gradient-to-r from-secondary-50 to-primary-50 dark:from-secondary-900/20 dark:to-primary-900/20 rounded-xl border border-secondary-200 dark:border-secondary-800">
                <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                  {t('guide.smartDayScheduling')}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300">
                  {t('guide.daySchedulingDesc')}
                </p>
              </div>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <img src="/assets/7-add-tracker-types.png" alt="Add Tracker Types Screenshot" className="rounded-xl shadow-lg w-full" />
                <img src="/assets/14-tracker-details-visualize.png" alt="Tracker Details Visualize Screenshot" className="rounded-xl shadow-lg w-full" />
              </div>
            </section>

            {/* Spark Points & Gamification */}
            <section className="glass-card p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mr-4">
                  <svg className="w-6 h-6" fill="#fbbf24" viewBox="0 0 24 24">
                    <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {t('guide.sparkPointsTitle')}
                </h2>
              </div>
              <p className="text-neutral-600 dark:text-neutral-300 mb-4">
                {t('guide.sparkPointsDesc')}
              </p>
            </section>
          </div>

          {/* General Features */}
          <div className="glass-card p-8 rounded-2xl mt-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                {t('guide.generalFeaturesTitle')}
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-3">
                  {t('guide.userExperience')}
                </h3>
                <ul className="space-y-2 text-neutral-600 dark:text-neutral-300">
                  <li> {t('guide.darkModeSupport')}</li>
                  <li> {t('guide.multiLanguage')}</li>
                  <li> {t('guide.mobileResponsive')}</li>
                  <li> {t('guide.offlineReady')}</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-3">
                  {t('guide.dataSecurity')}
                </h3>
                <ul className="space-y-2 text-neutral-600 dark:text-neutral-300">
                  <li> {t('guide.localStorage')}</li>
                  <li> {t('guide.privacyFirst')}</li>
                  <li> {t('guide.freeForever')}</li>
                  <li> {t('guide.openSource')}</li>
                  <li> {t('guide.dataSync')}</li>
                  <li> {t('guide.completelyFree')}</li>
                </ul>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <img src="/assets/8-home-page.png" alt="Home Page Screenshot" className="rounded-xl shadow-lg w-full" />
              <img src="/assets/9-profile-page.png" alt="Profile Page Screenshot" className="rounded-xl shadow-lg w-full" />
              <img src="/assets/4-profile-darkmode.png" alt="Profile Darkmode Screenshot" className="rounded-xl shadow-lg w-full" />
              <img src="/assets/5-app-day-view-light.png" alt="App Day View Light Screenshot" className="rounded-xl shadow-lg w-full" />
              <img src="/assets/6-app-day-view-2.png" alt="App Day View 2 Screenshot" className="rounded-xl shadow-lg w-full" />
            </div>
          </div>

          {/* Language Support */}
          <div className="glass-card p-8 rounded-2xl mt-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5h12M9 3v2m1.048 9.5A18.22 18.22 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                {t('guide.multiLanguageTitle')}
              </h2>
            </div>
            <p className="text-neutral-600 dark:text-neutral-300 mb-4">
              {t('guide.multiLanguageDesc')}
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                  {t('guide.supportedLanguages')}
                </h3>
                <ul className="space-y-2 text-neutral-600 dark:text-neutral-300">
                  <li> {t('guide.englishDefault')}</li>
                  <li> {t('guide.spanishUS')}</li>
                  <li> {t('guide.englishIndia')}</li>
                  <li> {t('guide.hindi')}</li>
                  <li> {t('guide.french')}</li>
                  <li> {t('guide.marathi')}</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                  {t('guide.features')}
                </h3>
                <ul className="space-y-2 text-neutral-600 dark:text-neutral-300">
                  <li> {t('guide.autoLanguageDetection')}</li>
                  <li> {t('guide.manualLanguageSelection')}</li>
                  <li> {t('guide.persistentPreferences')}</li>
                  <li> {t('guide.localizedUI')}</li>
                  <li> {t('guide.rtlReady')}</li>
                </ul>
              </div>
            </div>
            <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
              <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                {t('guide.howToChangeLanguage')}
              </h3>
              <p className="text-neutral-600 dark:text-neutral-300">
                {t('guide.changeLanguageDesc')}
              </p>
            </div>
            <div className="mt-6">
              <img src="/assets/15-language-selector.png" alt="Language Selector Screenshot" className="rounded-xl shadow-lg w-full md:max-w-md md:mx-auto md:block" />
            </div>
          </div>

          {/* Motivation Section */}
          <div className="mt-12 space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="glass-card p-6 rounded-2xl text-center">
                <blockquote className="text-lg italic text-neutral-700 dark:text-neutral-200 mb-4">
                  "{t('guide.motivationQuote1')}"
                </blockquote>
                <cite className="text-neutral-500 dark:text-neutral-400">
                  {t('guide.motivationAuthor1')}
                </cite>
              </div>
              <div className="glass-card p-6 rounded-2xl text-center">
                <blockquote className="text-lg italic text-neutral-700 dark:text-neutral-200 mb-4">
                  "{t('guide.motivationQuote2')}"
                </blockquote>
                <cite className="text-neutral-500 dark:text-neutral-400">
                  {t('guide.motivationAuthor2')}
                </cite>
              </div>
              <div className="glass-card p-6 rounded-2xl text-center">
                <blockquote className="text-lg italic text-neutral-700 dark:text-neutral-200 mb-4">
                  "{t('guide.motivationQuote3')}"
                </blockquote>
                <cite className="text-neutral-500 dark:text-neutral-400">
                  {t('guide.motivationAuthor3')}
                </cite>
              </div>
              <div className="glass-card p-6 rounded-2xl text-center">
                <blockquote className="text-lg italic text-neutral-700 dark:text-neutral-200 mb-4">
                  "{t('guide.motivationQuote4')}"
                </blockquote>
                <cite className="text-neutral-500 dark:text-neutral-400">
                  {t('guide.motivationAuthor4')}
                </cite>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="mt-12 text-center">
            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">
                {t('guide.ctaTitle')}
              </h2>
              <p className="text-neutral-600 dark:text-neutral-300 mb-6">
                {t('guide.ctaDesc')}
              </p>
              <div className="flex justify-center">
                <button
                  onClick={() => navigate("/app")}
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold rounded-xl shadow-glow hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5"
                >
                  {t('guide.openApp')}
                  <svg
                    className="w-5 h-5 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
        <SocialHandles />
      </div>
    </>
  );
};

export default Guide;
